import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ManagerNavigation from './ManagerNavigation';
import ManagerDashboard from './ManagerDashboard';
import POSDashboard from '../pos/POSDashboard';
import BillingPaymentApp from '../pos/BillingPaymentApp';
import OrdersApp from '../pos/OrdersApp';
import InvoicesApp from '../pos/InvoicesApp';
import CRMApp from '../pos/CRMApp';
import ReportsApp from '../pos/ReportsApp';
import MenuManagement from '../dashboard/MenuManagement';
import WaiterManagement from '../WaiterManagement';
import MenuPreview from '../MenuPreview';
import TerminalSetup from '../pos/TerminalSetup';
import InventoryApp from '../inventory/InventoryApp';
import { restaurantService } from '../../services/dataService';

const PATH_TO_TAB = {
  '/manager-dashboard': 'manager-dashboard',
  '/menu': 'menu',
  '/waiters': 'waiters',
  '/preview': 'preview',
  '/pos-dashboard': 'pos-dashboard',
  '/billing-payment': 'billing-payment',
  '/orders': 'orders',
  '/invoices': 'invoices',
  '/reports': 'reports',
  '/crm': 'crm',
  '/terminal': 'terminal',
  '/inventory': 'inventory',
};

const ManagerApp = ({ restaurant, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = PATH_TO_TAB[location.pathname] || 'manager-dashboard';
  const [restaurantData] = useState(restaurant);
  const [menu, setMenu] = useState([]);

  // managerId is used to stamp invoices and filter reports
  const managerId = user?.managerId || user?.id;

  const handleTabChange = (newTab) => navigate(`/${newTab}`);

  useEffect(() => {
    if (restaurantData?.id) loadMenu();
  }, [restaurantData]);

  const loadMenu = async () => {
    const { data } = await restaurantService.getMenu(restaurantData.id);
    setMenu(data || []);
  };

  useEffect(() => {
    const titles = {
      'manager-dashboard': 'Dashboard',
      menu: 'Menu Management',
      waiters: 'Waiter Management',
      preview: 'Menu Preview',
      'pos-dashboard': 'POS Dashboard',
      'billing-payment': 'Quick Billing',
      orders: 'Orders',
      invoices: 'Invoices',
      reports: 'Reports',
      crm: 'Customers',
      terminal: 'Terminal',
      inventory: 'Inventory',
    };
    document.title = `${titles[activeTab] || 'Dashboard'} - ${restaurantData?.name || 'Menuron'}`;
  }, [activeTab, restaurantData]);

  useEffect(() => {
    const preventInstall = (e) => e.preventDefault();
    window.addEventListener('beforeinstallprompt', preventInstall);
    return () => window.removeEventListener('beforeinstallprompt', preventInstall);
  }, []);

  if (restaurantData?.menuron_lite) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '40px 32px', textAlign: 'center', maxWidth: '420px', width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📱</div>
          <h2 style={{ margin: '0 0 12px', color: '#111', fontSize: '1.4rem', fontWeight: '700' }}>Menuron Lite</h2>
          <p style={{ margin: '0 0 28px', color: '#666', lineHeight: '1.6' }}>
            This restaurant is on the Menuron Lite plan. Please use the Menuron Android app to access your account.
          </p>
          <button
            onClick={onLogout}
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 28px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'manager-dashboard':
        return <ManagerDashboard restaurant={restaurantData} managerId={managerId} managerName={user?.name} />;
      case 'menu':
        return <MenuManagement restaurant={restaurantData} menu={menu} onDataChange={loadMenu} />;
      case 'waiters':
        return <WaiterManagement restaurant={restaurantData} />;
      case 'preview':
        return <MenuPreview restaurant={restaurantData} />;
      default:
        break;
    }


    switch (activeTab) {
      case 'pos-dashboard':
        return <POSDashboard restaurant={restaurantData} managerId={managerId} />;
      case 'billing-payment':
        return <BillingPaymentApp restaurant={restaurantData} managerId={managerId} />;
      case 'orders':
        return <OrdersApp restaurant={restaurantData} />;
      case 'invoices':
        return <InvoicesApp restaurant={restaurantData} managerId={managerId} />;
      case 'reports':
        return <ReportsApp restaurant={restaurantData} userType="manager" managerId={managerId} managerName={user?.name} />;
      case 'crm':
        return <CRMApp restaurant={restaurantData} canManageDiscounts={false} />;
      case 'terminal':
        return <TerminalSetup restaurant={restaurantData} />;
      case 'inventory':
        return <InventoryApp restaurant={restaurantData} />;
      default:
        return <ManagerDashboard restaurant={restaurantData} managerId={managerId} managerName={user?.name} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <ManagerNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        restaurant={restaurantData}
        managerName={user?.name}
        onLogout={onLogout}
        posEnabled={true}
      />
      {renderContent()}
    </div>
  );
};

export default ManagerApp;
